import type {
  ApartmentSummary,
  ApartmentDetail,
  ApartmentQrCodeSummary,
  UpdateApartmentQrContentInput,
  AuditEventSummary,
  ArrivalDepartureItem,
  ApiEnvelope,
  BookingDetail,
  BookingGroupDetail,
  BookingSummary,
  BookingComCommissionReport,
  BookingComImportResult,
  CancelBookingInput,
  DismissCalendarBlockInput,
  CalendarChannelProvider,
  CalendarImportFeedSummary,
  CalendarSyncSummary,
  CashShiftSummary,
  CloseCashShiftInput,
  CloseOperationalShiftInput,
  CreateTeamInvitationInput,
  AcceptTeamInvitationInput,
  CreateApartmentInput,
  CreateBookingInput,
  CreateBookingGroupInput,
  CreateCalendarImportInput,
  CompleteMovementInput,
  CreateReportScheduleInput,
  CreateEmployeeInput,
  CreateEmployeeDocumentInput,
  CreateGuestInput,
  CreateGuestDocumentInput,
  CreateHousekeepingTaskInput,
  CreateOperationalExpenseInput,
  CreatePaymentInput,
  CreateRefundInput,
  CreateRoomServiceTaskInput,
  DashboardSummary,
  EmployeeSummary,
  EmployeeDocumentFile,
  EmployeeDocumentSummary,
  EmployeeTelegramLink,
  GuestSummary,
  GuestDetail,
  GuestQrLanding,
  GuestQrRequestInput,
  GuestQrRequestResult,
  GuestRequestSummary,
  GuestDocumentFile,
  GuestDocumentSummary,
  HousekeepingPerformanceReport,
  HousekeepingReviewInput,
  HousekeepingTaskSummary,
  IntegrationSyncEventSummary,
  IntegrationSummary,
  AccessControlIntegrationSummary,
  AccessCredentialSummary,
  AccessEventSummary,
  ConfigureAccessControlInput,
  SmartLockGatewaySummary,
  SmartLockSummary,
  OpenCashShiftInput,
  OperationalShiftSummary,
  OperationalShiftTemplate,
  AcceptOperationalShiftInput,
  ShiftChecklist,
  OperationalExpenseReceiptFile,
  OperationalExpenseSummary,
  OperationalShiftReportSummary,
  PaymentSummary,
  PropertySettings,
  PropertyTimeSettings,
  ReportSummary,
  ReportScheduleSummary,
  RoomServiceTaskSummary,
  SearchResult,
  TeamInvitationSummary,
  UpdateApartmentInput,
  UpdateBookingInput,
  UpdateBookingGroupInput,
  UpdateCalendarImportInput,
  ExternalCalendarBlockSummary,
  UpdateEmployeeInput,
  UpdateGuestInput,
  UpdateHousekeepingTaskInput,
  UpdateRoomServiceTaskInput,
} from "@apartos/contracts";

const baseUrl = import.meta.env.VITE_API_URL ?? "";

async function request<T>(path: string): Promise<T> {
  const response = await fetch(`${baseUrl}${path}`, {
    headers: { Accept: "application/json" },
    credentials: "include",
  });
  if (!response.ok) throw new Error(`API ${response.status}`);
  const payload = (await response.json()) as ApiEnvelope<T>;
  return payload.data;
}

async function mutate<T, TBody>(path: string, body: TBody): Promise<T> {
  const response = await fetch(`${baseUrl}${path}`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  const payload = await response.json() as ApiEnvelope<T> | { error: string };
  if (!response.ok) throw new Error("error" in payload ? payload.error : `API ${response.status}`);
  return (payload as ApiEnvelope<T>).data;
}

function guestSessionId(): string {
  const key = "apartos_guest_session";
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const value = crypto.randomUUID();
  sessionStorage.setItem(key, value);
  return value;
}

async function publicRequest<T>(path: string): Promise<T> {
  const response = await fetch(`${baseUrl}${path}`, {
    headers: { Accept: "application/json", "x-guest-session": guestSessionId() },
  });
  const payload = await response.json() as ApiEnvelope<T> | { error: string };
  if (!response.ok) throw new Error("error" in payload ? payload.error : `API ${response.status}`);
  return (payload as ApiEnvelope<T>).data;
}

async function publicMutate<T, TBody>(path: string, body: TBody): Promise<T> {
  const response = await fetch(`${baseUrl}${path}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-guest-session": guestSessionId(),
    },
    body: JSON.stringify(body),
  });
  const payload = await response.json() as ApiEnvelope<T> | { error: string };
  if (!response.ok) throw new Error("error" in payload ? payload.error : `API ${response.status}`);
  return (payload as ApiEnvelope<T>).data;
}

async function command(path: string, body?: unknown): Promise<void> {
  const response = await fetch(`${baseUrl}${path}`, {
    method: "POST",
    headers: body === undefined ? { Accept: "application/json" } : { Accept: "application/json", "Content-Type": "application/json" },
    credentials: "include",
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(payload.error ?? `API ${response.status}`);
  }
}

async function remove(path: string): Promise<void> {
  const response = await fetch(`${baseUrl}${path}`, {
    method: "DELETE",
    headers: { Accept: "application/json" },
    credentials: "include",
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(payload.error ?? `API ${response.status}`);
  }
}

async function update<T, TBody>(path: string, body: TBody): Promise<T> {
  const response = await fetch(`${baseUrl}${path}`, {
    method: "PUT",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  const payload = await response.json() as ApiEnvelope<T> | { error: string };
  if (!response.ok) throw new Error("error" in payload ? payload.error : `API ${response.status}`);
  return (payload as ApiEnvelope<T>).data;
}

async function download(path: string, filename: string): Promise<void> {
  const response = await fetch(`${baseUrl}${path}`, { credentials: "include" });
  if (!response.ok) throw new Error(`API ${response.status}`);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export const api = {
  dashboard: () => request<DashboardSummary>("/v1/dashboard/summary"),
  search: (query: string) => request<SearchResult[]>(`/v1/search?q=${encodeURIComponent(query)}`),
  // Operational screens do not need the large base64 photo galleries.
  apartments: () => request<ApartmentSummary[]>("/v1/apartments?compact=1"),
  apartmentCoverUrl: (id: string) => `${baseUrl}/v1/apartments/${encodeURIComponent(id)}/cover`,
  apartment: (id: string) => request<ApartmentDetail>(`/v1/apartments/${encodeURIComponent(id)}`),
  createApartment: (input: CreateApartmentInput) => mutate<ApartmentSummary, CreateApartmentInput>("/v1/apartments", input),
  updateApartment: (id: string, input: UpdateApartmentInput) => update<ApartmentSummary, UpdateApartmentInput>(`/v1/apartments/${encodeURIComponent(id)}`, input),
  deleteApartment: (id: string) => remove(`/v1/apartments/${encodeURIComponent(id)}`),
  apartmentQrCodes: (id: string) => request<ApartmentQrCodeSummary[]>(`/v1/apartments/${encodeURIComponent(id)}/qr-codes`),
  qrCodes: () => request<ApartmentQrCodeSummary[]>("/v1/qr-codes"),
  regenerateApartmentQrCode: (apartmentId: string, qrCodeId: string) =>
    mutate<ApartmentQrCodeSummary, Record<string, never>>(`/v1/apartments/${encodeURIComponent(apartmentId)}/qr-codes/${encodeURIComponent(qrCodeId)}/regenerate`, {}),
  updateApartmentQrContent: (apartmentId: string, qrCodeId: string, input: UpdateApartmentQrContentInput) =>
    update<ApartmentQrCodeSummary, UpdateApartmentQrContentInput>(`/v1/apartments/${encodeURIComponent(apartmentId)}/qr-codes/${encodeURIComponent(qrCodeId)}/content`, input),
  guestQr: (token: string) => publicRequest<GuestQrLanding>(`/v1/public/qr/${encodeURIComponent(token)}`),
  createGuestQrRequest: (token: string, input: GuestQrRequestInput) =>
    publicMutate<GuestQrRequestResult, GuestQrRequestInput>(`/v1/public/qr/${encodeURIComponent(token)}/request`, input),
  guestRequests: () => request<GuestRequestSummary[]>("/v1/guest-requests?limit=100"),
  updateGuestRequest: (id: string, status: GuestRequestSummary["status"]) =>
    update<GuestRequestSummary, { status: GuestRequestSummary["status"] }>(`/v1/guest-requests/${encodeURIComponent(id)}`, { status }),
  bookings: () => request<BookingSummary[]>("/v1/bookings"),
  calendarSync: () => request<CalendarSyncSummary>("/v1/calendar-sync"),
  ensureCalendarExports: (provider: CalendarChannelProvider = "booking") =>
    mutate<CalendarSyncSummary, { provider: CalendarChannelProvider }>("/v1/calendar-sync/exports", { provider }),
  regenerateCalendarExport: (id: string) =>
    mutate<CalendarSyncSummary, Record<string, never>>(`/v1/calendar-sync/exports/${encodeURIComponent(id)}/regenerate`, {}),
  createCalendarImport: (input: CreateCalendarImportInput) =>
    mutate<CalendarImportFeedSummary, CreateCalendarImportInput>("/v1/calendar-sync/imports", input),
  updateCalendarImport: (id: string, input: UpdateCalendarImportInput) =>
    update<CalendarImportFeedSummary, UpdateCalendarImportInput>(`/v1/calendar-sync/imports/${encodeURIComponent(id)}`, input),
  deleteCalendarImport: (id: string) => remove(`/v1/calendar-sync/imports/${encodeURIComponent(id)}`),
  syncCalendarImport: (id: string) =>
    mutate<CalendarImportFeedSummary, Record<string, never>>(`/v1/calendar-sync/imports/${encodeURIComponent(id)}/sync`, {}),
  calendarBlocks: () => request<ExternalCalendarBlockSummary[]>("/v1/calendar-sync/blocks"),
  dismissCalendarBlock: (id: string, input: DismissCalendarBlockInput) =>
    mutate<ExternalCalendarBlockSummary[], DismissCalendarBlockInput>(`/v1/calendar-sync/blocks/${encodeURIComponent(id)}/dismiss`, input),
  importBookingCom: (input: { fileName?: string; contentBase64: string; dryRun: boolean }) =>
    mutate<BookingComImportResult, { fileName?: string; contentBase64: string; dryRun: boolean }>("/v1/integrations/booking-com/import", input),
  bookingComCommission: (month: string) =>
    request<BookingComCommissionReport>(`/v1/integrations/booking-com/commission?month=${encodeURIComponent(month)}`),
  booking: (id: string) => request<BookingDetail>(`/v1/bookings/${encodeURIComponent(id)}`),
  createBooking: (input: CreateBookingInput) => mutate<BookingSummary, CreateBookingInput>("/v1/bookings", input),
  createBookingGroup: (input: CreateBookingGroupInput) => mutate<BookingGroupDetail, CreateBookingGroupInput>("/v1/booking-groups", input),
  bookingGroup: (id: string) => request<BookingGroupDetail>(`/v1/booking-groups/${encodeURIComponent(id)}`),
  updateBookingGroup: (id: string, input: UpdateBookingGroupInput) => update<BookingGroupDetail, UpdateBookingGroupInput>(`/v1/booking-groups/${encodeURIComponent(id)}`, input),
  updateBooking: (id: string, input: UpdateBookingInput) => update<BookingSummary, UpdateBookingInput>(`/v1/bookings/${encodeURIComponent(id)}`, input),
  cancelBooking: (id: string, input: CancelBookingInput) => mutate<BookingSummary, CancelBookingInput>(`/v1/bookings/${encodeURIComponent(id)}/cancel`, input),
  guests: () => request<GuestSummary[]>("/v1/guests"),
  guest: (id: string) => request<GuestDetail>(`/v1/guests/${encodeURIComponent(id)}`),
  createGuest: (input: CreateGuestInput) => mutate<GuestSummary, CreateGuestInput>("/v1/guests", input),
  updateGuest: (id: string, input: UpdateGuestInput) => update<GuestSummary, UpdateGuestInput>(`/v1/guests/${encodeURIComponent(id)}`, input),
  guestDocuments: (id: string) => request<GuestDocumentSummary[]>(`/v1/guests/${encodeURIComponent(id)}/documents`),
  createGuestDocument: (id: string, input: CreateGuestDocumentInput) => mutate<GuestDocumentSummary, CreateGuestDocumentInput>(`/v1/guests/${encodeURIComponent(id)}/documents`, input),
  guestDocumentFile: (guestId: string, documentId: string) => request<GuestDocumentFile>(`/v1/guests/${encodeURIComponent(guestId)}/documents/${encodeURIComponent(documentId)}/file`),
  deleteGuestDocument: (guestId: string, documentId: string) => command(`/v1/guests/${encodeURIComponent(guestId)}/documents/${encodeURIComponent(documentId)}/delete`),
  movements: () => request<ArrivalDepartureItem[]>("/v1/operations/movements"),
  completeMovement: (id: string, input: CompleteMovementInput) =>
    command(`/v1/operations/movements/${encodeURIComponent(id)}/complete`, input),
  markNoShow: (id: string) =>
    command(`/v1/operations/movements/${encodeURIComponent(id)}/no-show`),
  currentOperationalShift: () => request<OperationalShiftSummary | null>("/v1/operations/shifts/current"),
  operationalShiftOptions: () => request<OperationalShiftTemplate[]>("/v1/operations/shifts/options"),
  acceptOperationalShift: (templateId: string, checklist: ShiftChecklist) =>
    mutate<OperationalShiftSummary, AcceptOperationalShiftInput>("/v1/operations/shifts/accept", { templateId, checklist }),
  closeOperationalShift: () =>
    mutate<OperationalShiftSummary, CloseOperationalShiftInput>("/v1/operations/shifts/close", {}),
  closeOperationalShiftWithReport: (input: CloseOperationalShiftInput) =>
    mutate<OperationalShiftSummary, CloseOperationalShiftInput>("/v1/operations/shifts/close", input),
  operationalExpenses: (shiftId?: string) =>
    request<OperationalExpenseSummary[]>(`/v1/operations/expenses${shiftId ? `?shiftId=${encodeURIComponent(shiftId)}` : ""}`),
  createOperationalExpense: (input: CreateOperationalExpenseInput) =>
    mutate<OperationalExpenseSummary, CreateOperationalExpenseInput>("/v1/operations/expenses", input),
  operationalExpenseReceipt: (id: string) =>
    request<OperationalExpenseReceiptFile>(`/v1/operations/expenses/${encodeURIComponent(id)}/receipt`),
  operationalShiftReports: () => request<OperationalShiftReportSummary[]>("/v1/operations/shifts/reports"),
  housekeeping: () => request<HousekeepingTaskSummary[]>("/v1/housekeeping/tasks"),
  createHousekeeping: (input: CreateHousekeepingTaskInput) => mutate<HousekeepingTaskSummary, CreateHousekeepingTaskInput>("/v1/housekeeping/tasks", input),
  updateHousekeeping: (id: string, input: UpdateHousekeepingTaskInput) => update<HousekeepingTaskSummary, UpdateHousekeepingTaskInput>(`/v1/housekeeping/tasks/${encodeURIComponent(id)}`, input),
  expressHousekeeping: (id: string) =>
    command(`/v1/housekeeping/tasks/${encodeURIComponent(id)}/express`, {}),
  completeHousekeeping: (id: string) =>
    command(`/v1/housekeeping/tasks/${encodeURIComponent(id)}/complete`),
  reviewHousekeeping: (id: string, input: HousekeepingReviewInput) =>
    mutate<HousekeepingTaskSummary, HousekeepingReviewInput>(`/v1/housekeeping/tasks/${encodeURIComponent(id)}/review`, input),
  housekeepingPerformance: (from: string, to: string) =>
    request<HousekeepingPerformanceReport>(`/v1/housekeeping/performance?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  roomService: () => request<RoomServiceTaskSummary[]>("/v1/room-service/tasks"),
  createRoomService: (input: CreateRoomServiceTaskInput) => mutate<RoomServiceTaskSummary, CreateRoomServiceTaskInput>("/v1/room-service/tasks", input),
  updateRoomService: (id: string, input: UpdateRoomServiceTaskInput) => update<RoomServiceTaskSummary, UpdateRoomServiceTaskInput>(`/v1/room-service/tasks/${encodeURIComponent(id)}`, input),
  payments: () => request<PaymentSummary[]>("/v1/payments"),
  cashShifts: () => request<CashShiftSummary[]>("/v1/payments/cash-shifts"),
  openCashShift: (input: OpenCashShiftInput) => mutate<CashShiftSummary, OpenCashShiftInput>("/v1/payments/cash-shifts/open", input),
  closeCashShift: (id: string, input: CloseCashShiftInput) => mutate<CashShiftSummary, CloseCashShiftInput>(`/v1/payments/cash-shifts/${encodeURIComponent(id)}/close`, input),
  createPayment: (input: CreatePaymentInput) => mutate<PaymentSummary, CreatePaymentInput>("/v1/payments", input),
  refundPayment: (id: string, input: CreateRefundInput) => mutate<PaymentSummary, CreateRefundInput>(`/v1/payments/${encodeURIComponent(id)}/refund`, input),
  exportPayments: () => download("/v1/payments/export.csv", "qadim-yard-payments.csv"),
  reports: () => request<ReportSummary>("/v1/reports/summary"),
  reportSchedules: () => request<ReportScheduleSummary[]>("/v1/reports/schedules"),
  createReportSchedule: (input: CreateReportScheduleInput) => mutate<ReportScheduleSummary, CreateReportScheduleInput>("/v1/reports/schedules", input),
  setReportScheduleActive: (id: string, isActive: boolean) => mutate<ReportScheduleSummary, { isActive: boolean }>(`/v1/reports/schedules/${encodeURIComponent(id)}/status`, { isActive }),
  exportReports: () => download("/v1/reports/export.csv", "qadim-yard-bookings.csv"),
  exportReportsXlsx: () => download("/v1/reports/export.xlsx", "qadim-yard-bookings.xlsx"),
  exportReportsPdf: () => download("/v1/reports/export.pdf", "qadim-yard-report.pdf"),
  employees: () => request<EmployeeSummary[]>("/v1/employees"),
  createEmployee: (input: CreateEmployeeInput) => mutate<EmployeeSummary, CreateEmployeeInput>("/v1/employees", input),
  updateEmployee: (id: string, input: UpdateEmployeeInput) => update<EmployeeSummary, UpdateEmployeeInput>(`/v1/employees/${encodeURIComponent(id)}`, input),
  createEmployeeTelegramLink: (id: string) => mutate<EmployeeTelegramLink, Record<string, never>>(`/v1/employees/${encodeURIComponent(id)}/telegram-link`, {}),
  disconnectEmployeeTelegram: (id: string) => mutate<EmployeeSummary, Record<string, never>>(`/v1/employees/${encodeURIComponent(id)}/telegram-disconnect`, {}),
  employeeDocuments: (id: string) => request<EmployeeDocumentSummary[]>(`/v1/employees/${encodeURIComponent(id)}/documents`),
  createEmployeeDocument: (id: string, input: CreateEmployeeDocumentInput) => mutate<EmployeeDocumentSummary, CreateEmployeeDocumentInput>(`/v1/employees/${encodeURIComponent(id)}/documents`, input),
  employeeDocumentFile: (employeeId: string, documentId: string) => request<EmployeeDocumentFile>(`/v1/employees/${encodeURIComponent(employeeId)}/documents/${encodeURIComponent(documentId)}/file`),
  deleteEmployeeDocument: (employeeId: string, documentId: string) => command(`/v1/employees/${encodeURIComponent(employeeId)}/documents/${encodeURIComponent(documentId)}/delete`),
  invitations: () => request<TeamInvitationSummary[]>("/v1/invitations"),
  createInvitation: (input: CreateTeamInvitationInput) => mutate<TeamInvitationSummary, CreateTeamInvitationInput>("/v1/invitations", input),
  revokeInvitation: (id: string) => command(`/v1/invitations/${encodeURIComponent(id)}/revoke`),
  acceptInvitation: (input: AcceptTeamInvitationInput) => mutate<EmployeeSummary, AcceptTeamInvitationInput>("/v1/invitations/accept", input),
  settings: () => request<PropertySettings>("/v1/settings"),
  updateSettings: (settings: PropertySettings) => update<PropertySettings, PropertySettings>("/v1/settings", settings),
  audit: () => request<AuditEventSummary[]>("/v1/audit?limit=50"),
  integrationEvents: () => request<IntegrationSyncEventSummary[]>("/v1/integrations/sync-events?limit=100"),
  retryIntegrationEvent: (id: string) => command(`/v1/integrations/sync-events/${encodeURIComponent(id)}/retry`),
  integrations: () => request<IntegrationSummary[]>("/v1/integrations"),
  setIntegrationEnabled: (id: string, isEnabled: boolean) => mutate<IntegrationSummary, { isEnabled: boolean }>(`/v1/integrations/${encodeURIComponent(id)}/status`, { isEnabled }),
  accessControl: () => request<AccessControlIntegrationSummary>("/v1/access-control/integration"),
  configureAccessControl: (input: ConfigureAccessControlInput) => mutate<AccessControlIntegrationSummary, ConfigureAccessControlInput>("/v1/access-control/integration", input),
  testAccessControl: () => command("/v1/access-control/integration/test"),
  syncSmartLocks: () => mutate<{ locks: number; gateways: number }, Record<string, never>>("/v1/access-control/sync", {}),
  smartLocks: (apartmentId?: string) => request<SmartLockSummary[]>(`/v1/access-control/locks${apartmentId ? `?apartmentId=${encodeURIComponent(apartmentId)}` : ""}`),
  smartLockGateways: () => request<SmartLockGatewaySummary[]>("/v1/access-control/gateways"),
  bindApartmentLocks: (apartmentId: string, input: { lockIds: string[]; pilotEnabled: boolean }) => command(`/v1/apartments/${encodeURIComponent(apartmentId)}/locks`, input),
  bookingAccess: (bookingId: string) => request<AccessCredentialSummary[]>(`/v1/bookings/${encodeURIComponent(bookingId)}/access`),
  revealAccessCredential: (id: string) => mutate<{ pin: string }, Record<string, never>>(`/v1/access-credentials/${encodeURIComponent(id)}/reveal`, {}),
  retryAccessCredential: (id: string) => command(`/v1/access-credentials/${encodeURIComponent(id)}/retry`),
  regenerateAccessCredential: (id: string) => command(`/v1/access-credentials/${encodeURIComponent(id)}/regenerate`),
  revokeAccessCredential: (id: string) => command(`/v1/access-credentials/${encodeURIComponent(id)}/revoke`),
  remoteUnlock: (id: string) => command(`/v1/smart-locks/${encodeURIComponent(id)}/unlock`, { confirmed: true }),
  accessEvents: () => request<AccessEventSummary[]>("/v1/access-control/events?limit=100"),
  propertyTimeSettings: () => request<PropertyTimeSettings>("/v1/property/time-settings"),
};
